import { useEffect, useRef } from 'react';
import loadable from '@loadable/component';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { routes } from './route-entries';
import { RouteEntryProvider, renderRouteEntries } from './route-utils';
import {
  useActiveTenant,
  useTenants,
  useSetCurrentTenant,
} from '~hooks/useTenant';
import { useAuthCheck } from '~services/auth';
import Page from '~components/navigation/Page';
import ProtectedRoute from '~components/navigation/ProtectedRoute';
import PageLayout from '~components/navigation/PageLayout';

const Login = loadable(() => import('./login'));
const NotFound = loadable(() => import('./not-found'));
const NotFoundAuthenticated = loadable(
  () => import('./not-found/index.authenticated')
);

// Loads tenant context as the first thing after authentication so the active
// tenant is available app-wide (e.g. the profile menu), and auto-selects the
// first available tenant for first-time users who have none selected yet.
function useBootstrapTenant(authStatus: string) {
  const { data: activeTenant } = useActiveTenant();
  const { data: tenantsPage } = useTenants();
  const setCurrentTenantMutation = useSetCurrentTenant();
  const queryClient = useQueryClient();
  const didRun = useRef(false);

  useEffect(() => {
    if (authStatus !== 'authenticated' || didRun.current) return;
    if (activeTenant === undefined || tenantsPage === undefined) return;
    didRun.current = true;

    // No tenant selected yet (first-time user): pick the first one available.
    // Token is already valid at bootstrap, so resetting queries in onSuccess
    // is safe here (no token refresh needed).
    if (!activeTenant?.tenantId && tenantsPage?.data?.length) {
      setCurrentTenantMutation.mutate(
        { tenantId: tenantsPage.data[0].tenantId },
        { onSuccess: () => queryClient.resetQueries() }
      );
    }
  }, [
    authStatus,
    activeTenant,
    tenantsPage,
    setCurrentTenantMutation,
    queryClient,
  ]);
}

export default function Router() {
  const authStatus = useAuthCheck();
  useBootstrapTenant(authStatus);
  return (
    <RouteEntryProvider routes={routes}>
      <Routes>
        <Route path="/" element={<ProtectedRoute />}>
          <Route
            path="/"
            element={
              <PageLayout>
                <Outlet />
              </PageLayout>
            }
          >
            {renderRouteEntries(routes)}

            <Route index element={<Navigate replace to="/admin" />} />

            <Route
              path="*"
              element={
                <Page>
                  <NotFoundAuthenticated />
                </Page>
              }
            />
          </Route>
        </Route>

        <Route
          path="login"
          element={
            authStatus === 'authenticated' ? (
              <Navigate to="/" replace />
            ) : (
              <Page>
                <Login />
              </Page>
            )
          }
        />

        <Route
          path="*"
          element={
            <Page>
              <NotFound />
            </Page>
          }
        />
      </Routes>
    </RouteEntryProvider>
  );
}
